import React, { useState, useCallback, useEffect } from "react";
import "../page/patientList.css";
import PatientHeader from "../component/patientHeader";
import PatientTable from "../component/patientTable";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import ModalAddPatient from "../component/modalAddPatient";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";
import PaginationFooter from "../../../components/footer/paginationFooter";
import { formatDate } from "../../../helper/formatDate";

const PatientList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddPatient, setIsOpenModalAddPatient] = useState(false);
  const [selectedPatientData, setSelectedPatientData] = useState({
    patient_id: "",
    patient_number: "",
    patient_name: "",
    patient_birthday: formatDate(new Date()),
    patient_gender: "",
    patient_address: "",
    patient_phone_number: "",
  });
  const [listPatientData, setListPatientData] = useState(null);
  const [isOpenModalDeletePatient, setIsOpenModalDeletePatient] =
    useState(false);
  const [idPatientToDelete, setIdPatientToDelete] = useState("");
  const [searchPatientKeyword, setSearchPatientKeyword] = useState("");

  // =====================
  // Function Definition
  // =====================

  const addNewPatient = () => {
    resetSelectedPatientDataValue();
    setIsOpenModalAddPatient(true);
  };

  const onEditPatient = (patientData) => {
    setSelectedPatientData(patientData);
    setIsOpenModalAddPatient(true);
  };

  const onDeletePatient = (patientData) => {
    setIsOpenModalDeletePatient(true);
    setIdPatientToDelete(patientData.patient_id);
  };

  const onPatientClick = (patientId) => {
    navigate(`/data-pasien/${patientId}`);
  };

  const onDeletePatientConfirmed = () => {
    setIsOpenModalDeletePatient(false);
    deletePatient();
  };

  const onCloseModalDeletePatient = () => {
    setIdPatientToDelete("");
    setIsOpenModalDeletePatient(false);
  };

  const onCloseModal = () => {
    resetSelectedPatientDataValue();
    setIsOpenModalAddPatient(false);
  };

  const onSubmitNewPatient = (patientData, isEditData) => {
    setIsOpenModalAddPatient(false);
    if (isEditData) {
      editPatient(patientData);
    } else {
      createNewPatient(patientData);
    }
  };

  const resetSelectedPatientDataValue = () => {
    setSelectedPatientData({
      patient_id: "",
      patient_name: "",
      patient_birthday: formatDate(new Date()),
      patient_gender: "",
      patient_address: "",
      patient_phone_number: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getPatientData = useCallback(async () => {
    try {
      setIsLoading(true);
      localStorage.setItem("searchValue", "");
      setSearchPatientKeyword("");
      const response = await fetch(
        Constant.BASE_API_URL + "/pasien?page=" + page,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const patients = data.patients;
      if (!patients) {
        setListPatientData([]);
      } else {
        setListPatientData(patients);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListPatientData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewPatient = async (patientData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/pasien", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          patient_name: patientData.patient_name,
          patient_birthday: patientData.patient_birthday,
          patient_gender: patientData.patient_gender,
          patient_address: patientData.patient_address,
          patient_phone_number: patientData.patient_phone_number,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getPatientData();
    }
  };

  const editPatient = async (patientData) => {
    try {
      setIsLoading(true);
      const patientId = patientData.patient_id;
      const response = await fetch(
        Constant.BASE_API_URL + "/pasien/" + patientId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            patient_number: patientData.patient_number,
            patient_name: patientData.patient_name,
            patient_birthday: patientData.patient_birthday,
            patient_gender: patientData.patient_gender,
            patient_address: patientData.patient_address,
            patient_phone_number: patientData.patient_phone_number,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getPatientData();
    }
  };

  const deletePatient = async () => {
    try {
      setIsLoading(true);
      const patientId = idPatientToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/pasien/" + patientId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdPatientToDelete("");
      getPatientData();
    }
  };

  const searchPatient = (searchText) => {
    setSearchPatientKeyword(searchText);
    setPage(1);
  };

  const getPatientSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/pasien/search");
      url.searchParams.append("keyword", searchPatientKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;
      const patients = data.patients;
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
      if (!patients) {
        setListPatientData([]);
      } else {
        setListPatientData(patients);
      }
    } catch (error) {
      setListPatientData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchPatientKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchPatientKeyword !== "") {
      getPatientSearchData();
    } else {
      getPatientData();
    }
  }, [getPatientData, getPatientSearchData, searchPatientKeyword]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Pasien</h1>
      <PatientHeader
        onAddPatientClicked={addNewPatient}
        onSearchPatient={searchPatient}
      />
      <PatientTable
        page={page}
        patients={listPatientData}
        onEditPatientClicked={onEditPatient}
        onDeletePatientClicked={onDeletePatient}
        onPatientClicked={onPatientClick}
      />
      <ModalAddPatient
        isShowModal={isOpenModalAddPatient}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewPatient}
        patientObject={selectedPatientData}
        key={Date.now()}
      />

      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeletePatient}
        onCloseModal={onCloseModalDeletePatient}
        onDeleteConfirmed={onDeletePatientConfirmed}
        idToDelete={idPatientToDelete}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
    </div>
  );
};

export default PatientList;
